import { useState, useCallback } from 'react';
import { apiV2 } from '../actions/index';
import Swal from 'sweetalert2';

export function useApiKeys() {
    const [apiKeys, setApiKeys] = useState([]);
    const [apiKey, setApiKey] = useState(null);
    const [availableScopes, setAvailableScopes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchAll = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiV2.get('/personal-access-tokens');
            setApiKeys(response.data.objects || []);
        } catch (err) {
            setError(err);
            Swal.fire({
                icon: 'warning',
                text: err.response?.data?.message || 'Failed to fetch API keys',
            });
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchOne = useCallback(async (id) => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiV2.get(`/personal-access-tokens/${id}`);
            setApiKey(response.data);
            return response.data;
        } catch (err) {
            setError(err);
            Swal.fire({
                icon: 'warning',
                text: err.response?.data?.message || 'Failed to fetch API key',
            });
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    const create = useCallback(async (data) => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiV2.post('/personal-access-tokens', data);
            return response.data;
        } catch (err) {
            setError(err);
            Swal.fire({
                icon: 'warning',
                text: err.response?.data?.message || 'Failed to create API key',
            });
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    const update = useCallback(async (id, data) => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiV2.put(`/personal-access-tokens/${id}`, data);
            Swal.fire({
                icon: 'success',
                title: 'API key updated!',
                timer: 2000,
            });
            return response.data;
        } catch (err) {
            setError(err);
            Swal.fire({
                icon: 'warning',
                text: err.response?.data?.message || 'Failed to update API key',
            });
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    const revoke = useCallback(async (id) => {
        setLoading(true);
        setError(null);
        try {
            await apiV2.delete(`/personal-access-tokens/${id}`);
            setApiKeys(prev => prev.filter(key => key.id !== id));
            Swal.fire({
                icon: 'success',
                title: 'API key revoked!',
                timer: 2000,
            });
            return true;
        } catch (err) {
            setError(err);
            Swal.fire({
                icon: 'warning',
                text: err.response?.data?.message || 'Failed to revoke API key',
            });
            return false;
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchAvailableScopes = useCallback(async () => {
        try {
            const response = await apiV2.get('/personal-access-tokens/available-scopes');
            setAvailableScopes(response.data || []);
        } catch {
            setAvailableScopes([]);
        }
    }, []);

    return { apiKeys, apiKey, availableScopes, loading, error, fetchAll, fetchOne, create, update, revoke, fetchAvailableScopes };
}
